/**
 * Hype Million — shared types.
 *
 * Chat plays as one collective contestant climbing a 15-question ladder.
 * Viewers vote A/B/C/D in chat; the most-voted answer is locked in when
 * the timer fires. Correct → ladder advances + correct voters split a gem
 * pot. Wrong (or zero votes) → game over, session ends.
 *
 * Round state lives in Redis (hot path), with the round flushed to DB at
 * resolve. Vote tally is a per-round Redis hash for race-safe increments.
 */

export const OPTIONS = ['A', 'B', 'C', 'D'] as const;
export type OptionLetter = (typeof OPTIONS)[number];

export const CATEGORIES = [
  'mixed',
  'general',
  'movies',
  'tv',
  'music',
  'video_games',
  'science',
  'history',
  'geography',
  'sports',
  'literature',
  'pop_culture',
] as const;
export type Category = (typeof CATEGORIES)[number];

export const CATEGORY_LABEL: Readonly<Record<Category, string>> = {
  mixed: 'Mixed',
  general: 'General',
  movies: 'Movies',
  tv: 'TV',
  music: 'Music',
  video_games: 'Video Games',
  science: 'Science',
  history: 'History',
  geography: 'Geography',
  sports: 'Sports',
  literature: 'Literature',
  pop_culture: 'Pop Culture',
};

export interface HypeMillionSettings {
  /** Base gem reward × LEVELS[i].gemMultiplier = per-question pot. */
  baseGemReward: number;
  /** Seconds chat has to vote on each question. */
  questionTimeoutSeconds: number;
  /** Seconds of "reveal" before auto-advance. */
  revealDurationSeconds: number;
  /** Seconds of game-over splash before idle. */
  endSplashSeconds: number;
  /** Category pool. 'mixed' = pick a random non-mixed per round. */
  category: Category;
  /** Auto-advance through reveal → next question. If false, streamer must skip. */
  autoAdvance: boolean;
  /** Minimum votes cast before the round resolves to a winner. Below this,
   *  ends the run as 'no-votes'. 0 = any count counts (including 0 = wrong). */
  minVotesToCount: number;
  /** Chat prefix override. NULL = default (bare letters + !letter accepted). */
  chatPrefix: string | null;
}

export const DEFAULT_SETTINGS: HypeMillionSettings = {
  baseGemReward: 100,
  questionTimeoutSeconds: 30,
  revealDurationSeconds: 6,
  endSplashSeconds: 10,
  category: 'mixed',
  autoAdvance: true,
  minVotesToCount: 1,
  chatPrefix: null,
};

/**
 * Server-side round state. The overlay snapshot strips `correctAnswer` so
 * chat can't inspect page source to cheat.
 */
export interface RoundState {
  roundId: string;
  sessionId: string;
  streamerId: string;
  level: number;
  /** Question text. */
  question: string;
  /** [A, B, C, D] option text. */
  options: [string, string, string, string];
  correctAnswer: OptionLetter;
  category: Exclude<Category, 'mixed'>;
  prizeGems: number;
  startedAt: number;
  /** Voting deadline (UTC ms). */
  deadline: number;
  /** Per-voter platform+id → letter, captured first-vote-only. */
  votes: Record<string, OptionLetter>;
  /** Aggregate counts. */
  tally: Record<OptionLetter, number>;
  phase: 'voting' | 'reveal' | 'ended';
  /** Locked-in answer (highest tally; tie → first-to-tie). null until reveal. */
  lockedAnswer: OptionLetter | null;
  /** Time the answer was locked (for reveal animation). */
  lockedAt: number | null;
  /** Was the locked answer correct? */
  correct: boolean | null;
}

/**
 * Session state — wraps the per-question rounds and ladder progress.
 * Lives in Redis between rounds; flushed to DB on round resolve.
 */
export interface SessionState {
  sessionId: string;
  streamerId: string;
  category: Exclude<Category, 'mixed'> | 'mixed';
  baseGemReward: number;
  currentLevel: number;
  startedAt: number;
  /** Total gems awarded across the session (sum of all per-round pots). */
  totalGemsAwarded: number;
  /** Cumulative per-voter gems for the per-session leaderboard. */
  leaderboard: Record<string, { username: string; gems: number; correct: number }>;
}

/** What the overlay sees. correctAnswer hidden until phase === 'reveal'. */
export interface GameSnapshot {
  sessionId: string;
  streamerId: string;
  /** Level we're rendering. */
  level: number;
  totalLevels: number;
  category: Exclude<Category, 'mixed'> | 'mixed';
  prizeGems: number;
  /** Per-level pot ladder so the overlay can render the side rail. */
  ladder: Array<{ level: number; prizeGems: number }>;
  question: string;
  options: [string, string, string, string];
  startedAt: number;
  deadline: number;
  phase: 'voting' | 'reveal' | 'ended';
  tally: Record<OptionLetter, number>;
  /** Total votes (sum of tally). */
  totalVotes: number;
  /** Reveal-only fields. */
  lockedAnswer: OptionLetter | null;
  correctAnswer: OptionLetter | null;
  correct: boolean | null;
  /** Per-question pot split among correct voters at reveal. */
  payout: {
    /** Gems each correct voter received. */
    perVoter: number;
    /** Total winners. */
    winners: number;
    /** Top 3 winners by username (display). */
    topWinners: Array<{ username: string; gems: number }>;
  } | null;
  /** When set (game over), terminal payload. */
  endState: {
    reason: 'win' | 'lose' | 'no-votes' | 'aborted';
    finalLevel: number;
    totalGemsAwarded: number;
    topPlayers: Array<{ username: string; gems: number; correct: number }>;
  } | null;
  /** Auto-advance countdown end (UTC ms) — overlay uses for next-question timer. */
  cooldownEndsAt: number | null;
}

export interface ChatVote {
  streamerId: string;
  viewerName: string;
  viewerPlatformId: string | null;
  platform: 'twitch' | 'youtube' | 'kick' | 'hypevault';
  /** Already parsed to A/B/C/D by the runner. */
  letter: OptionLetter;
}

/** Parse a chat message into a vote letter, or null if not a vote. Accepts
 *  bare letters ("A"), prefixed ("!a"), and numbered ("1"/"!1" → A). */
export function parseVote(message: string): OptionLetter | null {
  const m = message.trim().toLowerCase();
  if (!m) return null;
  // Strip a single leading "!" or "/" prefix.
  const stripped = m.replace(/^[!/]/, '').trim();
  if (stripped === 'a' || stripped === '1') return 'A';
  if (stripped === 'b' || stripped === '2') return 'B';
  if (stripped === 'c' || stripped === '3') return 'C';
  if (stripped === 'd' || stripped === '4') return 'D';
  return null;
}
